import { ThemeOptions } from '@mui/material';
import type {} from '@mui/x-date-pickers/themeAugmentation';

import { hexToRgb } from '@/utils/static/colors';

import colors from './colors';
import typography from './typography';

const overrides: ThemeOptions['components'] = {
  MuiButtonBase: {
    defaultProps: {
      disableTouchRipple: true,
      disableRipple: true,
    },
  },
  MuiButton: {
    defaultProps: {
      variant: 'contained',
      size: 'large',
    },
    styleOverrides: {
      root: {
        borderRadius: 16,
        maxHeight: 43,
        background: 'linear-gradient(180deg, #E7E7E7 0%, #FFF 35%)',
        boxShadow: 'none',
        '&:hover': {
          boxShadow: 'none',
        },
        '&:active': {
          boxShadow: 'none',
        },
      },
      sizeSmall: {
        ...typography.body3,
        padding: '8px',
      },
      sizeMedium: {
        ...typography.body2,
        padding: '12px',
      },
      sizeLarge: {
        ...typography.body1,
        padding: '16px',
      },
      contained: {
        position: 'relative',
        background: `linear-gradient(180deg, ${colors.graytone100} 0%, ${colors.white} 35%)`,
        color: colors.graytone950,
        borderColor: colors.white,
        fontWeight: 400,
        overflow: 'hidden',
        zIndex: 0,
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: `linear-gradient(180deg, ${colors.graytone200} 0%, ${colors.graytone50} 35%)`,
          opacity: 0,
          transition: 'opacity 300ms cubic-bezier(0.4, 0, 0.2, 1)',
          zIndex: -1,
        },
        '&:hover::before': {
          opacity: 1,
        },
        '&:hover': {
          borderColor: colors.graytone100,
        },
        '&:active::before': {
          background: `linear-gradient(180deg, ${colors.graytone300} 0%, ${colors.graytone100} 35%)`,
          opacity: 1,
        },
        '&:active': {
          borderColor: colors.graytone200,
        },
        '&:disabled': {
          background: `linear-gradient(180deg, ${colors.graytone200} 0%, ${colors.graytone50} 35%)`,
          borderColor: colors.graytone100,
          color: colors.graytone100,
        },
      },
      outlined: {
        position: 'relative',
        background: `linear-gradient(180deg, ${colors.graytone950} 0%, ${colors.black} 35%)`,
        color: colors.white,
        borderColor: colors.black,
        overflow: 'hidden',
        fontWeight: 400,
        zIndex: 0,
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: `linear-gradient(180deg, ${colors.graytone950} 0%, ${colors.graytone900} 35%)`,
          opacity: 0,
          transition: 'opacity 300ms cubic-bezier(0.4, 0, 0.2, 1)',
          zIndex: -1,
        },
        '&:hover::before': {
          opacity: 1,
        },
        '&:hover': {
          borderColor: colors.graytone900,
        },
        '&:active::before': {
          opacity: 1,
        },
        '&:active': {
          borderColor: colors.graytone900,
        },
        '&:disabled': {
          background: `linear-gradient(180deg, ${colors.graytone950} 0%, ${colors.black} 35%)`,
          color: colors.graytone700,
          borderColor: colors.black,
        },
      },
      text: {
        background: 'transparent',
        color: colors.white,
        border: 'none',
        '&:hover': {
          background: colors.graytone900,
        },
        '&:active': {
          background: colors.graytone800,
        },
        '&:disabled': {
          background: 'transparent',
          color: colors.graytone600,
        },
      },
    },
  },
  MuiIconButton: {
    defaultProps: {
      size: 'large',
    },
    styleOverrides: {
      sizeSmall: {
        height: 28,
        width: 28,
      },
      sizeMedium: {
        height: 36,
        width: 36,
      },
      sizeLarge: {
        height: 48,
        width: 48,
      },
    },
  },
  MuiToggleButtonGroup: {
    styleOverrides: {
      root: {
        overflowX: 'auto',
      },
    },
  },
  MuiToggleButton: {
    styleOverrides: {
      root: {
        backgroundColor: 'transparent',
        border: 'none',
        color: colors.graytone400,
        fontWeight: 400,
        padding: 10,
        ...typography.body1,
        marginRight: 12,
        '&:last-child': {
          marginRight: 0,
        },
        borderRadius: 10,
        '&:not(:first-of-type)': {
          borderTopLeftRadius: 10,
          borderBottomLeftRadius: 10,
        },
        '&:not(:last-of-type)': {
          borderTopRightRadius: 10,
          borderBottomRightRadius: 10,
        },
        '&:hover': {
          backgroundColor: colors.orange500,
          color: colors.white,
        },
        '&.Mui-selected': {
          backgroundColor: colors.orange500,
          color: colors.white,

          '&:hover': {
            backgroundColor: colors.orange600,
          },
        },
      },
    },
  },
  MuiContainer: {
    styleOverrides: {
      root: {
        height: '100%',
        '&:not(.MuiContainer-disableGutters)': {
          paddingLeft: 24,
          paddingRight: 24,

          '@media (max-width: 768px)': {
            paddingLeft: 12,
            paddingRight: 12,
          },
        },
      },
    },
  },
  MuiFab: {
    defaultProps: {
      size: 'medium',
    },
    styleOverrides: {
      root: {
        background: 'rgba(255, 255, 255, 0.05)',
        color: colors.graytone400,
        boxShadow: 'none',
        borderRadius: '54px',
        '&:hover': {
          background: 'rgba(255, 255, 255, 0.15)',
        },
        '&:active': {
          background: 'rgba(255, 255, 255, 0.25)',
        },
        '&:disabled': {
          backgroundColor: 'transaprent',
          color: colors.graytone800,
        },
      },
      sizeMedium: {
        width: 32,
        height: 32,
      },
    },
  },
  MuiSvgIcon: {
    defaultProps: {
      fontSize: 'large',
    },
    styleOverrides: {
      fontSizeSmall: {
        fontSize: 18,
      },
      fontSizeMedium: {
        fontSize: 20,
      },
      fontSizeLarge: {
        fontSize: 24,
      },
    },
    variants: [
      {
        props: { fontSize: 'small' },
        style: {
          fontSize: 16,
        },
      },
    ],
  },
  MuiPaper: {
    defaultProps: {
      elevation: 0,
    },
    styleOverrides: {
      rounded: {
        borderRadius: 10,
        padding: 16,
      },
      outlined: {
        borderColor: colors.graytone400,
      },
    },
  },
  MuiTextField: {
    defaultProps: {
      variant: 'filled',
    },
  },
  MuiPickersTextField: {
    defaultProps: {
      variant: 'filled',
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        color: colors.graytone400,
        border: 'none',
        borderBottom: `1px solid ${colors.graytone600}`,
        borderRadius: 0,
        backgroundColor: colors.graytone950,
        padding: '12px 0',
        '& .MuiSvgIcon-root': {
          color: colors.graytone400,
        },
        '&:hover': {
          borderBottom: `1px solid ${colors.graytone400}`,
          backgroundColor: colors.graytone900,
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
          '& .MuiSvgIcon-root': {
            color: colors.graytone400,
          },
        },
        '&.Mui-focused': {
          color: colors.white,
          borderBottom: `1px solid ${colors.white}`,
          backgroundColor: colors.graytone950,
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
          '& .MuiSvgIcon-root': {
            color: colors.white,
          },
        },
        '&.Mui-error': {
          borderBottom: `1px solid ${colors.orange500}`,
          backgroundColor: colors.orange950,
          color: colors.orange500,
        },
      },
      notchedOutline: {
        border: 'none',
      },
      input: {
        padding: 0,
        '&::placeholder': {
          opacity: 1,
          color: colors.graytone400,
        },
      },
      multiline: {
        padding: 0,
      },
    },
  },
  MuiPickersOutlinedInput: {
    styleOverrides: {
      root: {
        color: colors.graytone400,
        border: 'none',
        borderBottom: `1px solid ${colors.graytone600}`,
        borderRadius: 0,
        backgroundColor: colors.graytone950,
        padding: '12px 0',
        '& .MuiSvgIcon-root': {
          color: colors.graytone400,
        },
        '&:hover': {
          borderBottom: `1px solid ${colors.graytone400}`,
          backgroundColor: colors.graytone900,
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
          '& .MuiSvgIcon-root': {
            color: colors.graytone400,
          },
        },
        '&.Mui-focused': {
          color: colors.white,
          borderBottom: `1px solid ${colors.white}`,
          backgroundColor: colors.graytone950,
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
          '& .MuiSvgIcon-root': {
            color: colors.white,
          },
        },
        '&.Mui-error': {
          borderBottom: `1px solid ${colors.orange500}`,
          backgroundColor: colors.orange950,
          color: colors.orange500,
        },
      },
      notchedOutline: {
        border: 'none',
      },
      input: {
        padding: 0,
        '&::placeholder': {
          opacity: 1,
          color: colors.graytone400,
        },
      },
    },
  },
  MuiFilledInput: {
    defaultProps: {
      disableUnderline: true,
    },
    styleOverrides: {
      root: {
        color: colors.graytone400,
        border: 'none',
        borderBottom: `1px solid ${colors.graytone600}`,
        borderRadius: 0,
        backgroundColor: colors.graytone950,
        overflow: 'hidden',
        '&:hover:not(.Mui-error):not(.Mui-focused)': {
          borderBottom: `1px solid ${colors.graytone400}`,
          backgroundColor: colors.graytone900,
        },
        '&.Mui-focused:not(.Mui-error)': {
          color: colors.white,
          borderBottom: `1px solid ${colors.white}`,
          backgroundColor: colors.graytone950,
        },
        '&.Mui-error': {
          borderBottom: `1px solid ${colors.orange500}`,
          backgroundColor: colors.orange950,
          color: colors.orange500,
        },
        '&.Mui-error.Mui-focused': {
          borderBottom: `1px solid ${colors.orange500}`,
          backgroundColor: colors.orange950,
          color: colors.orange500,
        },
        '&.Mui-disabled': {
          color: colors.graytone100,
          backgroundColor: colors.graytone900,
          borderBottom: `1px solid ${colors.graytone100}`,
        },
      },
      input: {
        color: colors.white,
        '&.Mui-disabled': {
          WebkitTextFillColor: colors.graytone400,
        },
      },
    },
  },
  MuiPickersFilledInput: {
    defaultProps: {
      disableUnderline: true,
    },
    styleOverrides: {
      root: {
        color: colors.graytone400,
        border: 'none',
        borderBottom: `1px solid ${colors.graytone600}`,
        borderRadius: 0,
        backgroundColor: colors.graytone950,
        overflow: 'hidden',
        '&:hover:not(.Mui-error):not(.Mui-focused)': {
          borderBottom: `1px solid ${colors.graytone400}`,
          backgroundColor: colors.graytone900,
        },
        '&.Mui-focused:not(.Mui-error)': {
          color: colors.white,
          borderBottom: `1px solid ${colors.white}`,
          backgroundColor: colors.graytone950,
        },
        '&.Mui-error': {
          borderBottom: `1px solid ${colors.orange500}`,
          backgroundColor: colors.orange950,
          color: colors.orange500,
        },
        '&.Mui-error.Mui-focused': {
          borderBottom: `1px solid ${colors.orange500}`,
          backgroundColor: colors.orange950,
          color: colors.orange500,
        },
        '&.Mui-disabled': {
          color: colors.graytone100,
          backgroundColor: colors.graytone900,
          borderBottom: `1px solid ${colors.graytone100}`,
        },
      },
      input: {
        color: colors.white,
        '&.Mui-disabled': {
          WebkitTextFillColor: colors.graytone400,
        },
      },
    },
  },
  MuiInputBase: {
    styleOverrides: {
      input: {
        '&:-webkit-autofill': {
          transitionDelay: '9999s',
          transitionProperty: 'background-color, color',
        },
      },
    },
  },
  MuiPickersInputBase: {
    styleOverrides: {
      input: {
        '&:-webkit-autofill': {
          transitionDelay: '9999s',
          transitionProperty: 'background-color, color',
        },
      },
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      root: {
        ...typography.body1,
        color: colors.graytone400,
        '&.Mui-focused': {
          color: colors.white,
        },
        '&.Mui-error': {
          color: colors.orange500,
        },
        '&.Mui-disabled': {
          color: colors.graytone100,
        },
      },
      shrink: {
        ...typography.body1,
        color: colors.white,
      },
    },
  },
  MuiFormLabel: {
    styleOverrides: {
      root: {
        ...typography.body1,
        color: colors.graytone400,
        display: 'block',
        paddingBottom: 4,
        '&.Mui-focused': {
          color: colors.white,
        },
      },
    },
  },
  MuiFormControlLabel: {
    styleOverrides: {
      label: {
        ...typography.body1,
      },
    },
  },
  MuiFormHelperText: {
    styleOverrides: {
      root: {
        marginTop: 8,
        ...typography.body2,
        '&.Mui-error': {
          color: colors.orange500,
        },
      },
    },
  },
  MuiList: {
    styleOverrides: {
      root: {
        display: 'flex',
        flexDirection: 'column',
      },
    },
  },
  MuiListItemText: {
    styleOverrides: {
      root: {
        margin: 0,
      },
      primary: {
        ...typography.body1,
      },
    },
  },
  MuiListItemButton: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        '@media (hover: hover)': {
          '&:hover': {
            backgroundColor: colors.graytone100,
          },
        },
      },
    },
  },
  MuiListItemIcon: {
    styleOverrides: {
      root: {
        minWidth: 40,
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        padding: 6,
        borderRadius: 6,
        height: 'auto',
      },
      label: {
        ...typography.body1,
        paddingLeft: 6,
        paddingRight: 6,
        overflow: 'visible',
      },
    },
  },
  MuiCheckbox: {
    styleOverrides: {
      root: {
        color: colors.graytone600,
        borderRadius: '50%',
        transition: 'color 0.3s ease, background-color 0.3s ease',
        '&:hover': {
          backgroundColor: `rgba(${hexToRgb(colors.blue950)}, 0.8)`,
        },
        '&.Mui-checked': {
          color: colors.blue500,
        },
        '&.Mui-checked:hover': {
          backgroundColor: `rgba(${hexToRgb(colors.blue950)}, 0.8)`,
        },
      },
    },
  },
  MuiTabs: {
    styleOverrides: {
      root: {
        '& .MuiTabScrollButton-root.Mui-disabled': {
          display: 'none',
        },
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        ...typography.subtitle3,
        fontWeight: 500,
        color: colors.graytone300,
        '&:hover': {
          color: colors.graytone300,
        },
        '&.Mui-selected': {
          color: colors.white,
        },
      },
    },
  },
  MuiPaginationItem: {
    styleOverrides: {
      root: {
        borderRadius: 16,
        backgroundColor: colors.white,
        color: colors.graytone950,
        ...typography.body1,
        '&:hover': {
          background: `linear-gradient(180deg, ${colors.graytone200} 0%, ${colors.graytone50} 35%)`,
          color: colors.graytone950,
        },
        '&.Mui-selected': {
          background: `linear-gradient(180deg, ${colors.graytone300} 0%, ${colors.graytone100} 35%)`,
          color: colors.graytone950,
          cursor: 'default',
          pointerEvents: 'none',
        },
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: {
        padding: 0,
        maxWidth: 700,
        maxHeight: '80vh',

        '@media (max-width: 768px)': {
          maxHeight: '100%',
        },
      },
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: {
        padding: 24,
        color: colors.graytone950,

        '@media (max-width: 768px)': {
          padding: 16,
        },
      },
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: {
        padding: 24,
        color: colors.graytone950,

        '@media (max-width: 768px)': {
          padding: 16,
        },
      },
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: {
        padding: 24,
        gap: 16,

        '@media (max-width: 768px)': {
          flexDirection: 'column-reverse',
        },
      },
    },
  },
  MuiAvatar: {
    styleOverrides: {
      root: {
        width: 24,
        height: 24,
        fontWeight: 500,
        ...typography.body1,
      },
    },
  },
  MuiTypography: {
    defaultProps: {
      variant: 'body2',
      color: colors.white,
    },
  },
  MuiSnackbar: {
    defaultProps: {
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'center',
      },
    },
  },
  MuiAlert: {
    styleOverrides: {
      root: {
        backgroundColor: colors.graytone100,
        color: colors.white,
        alignItems: 'center',
      },
    },
  },
  MuiMenu: {
    styleOverrides: {
      paper: {
        borderRadius: 8,
        padding: 0,
      },
      list: {
        padding: 8,
      },
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        padding: 8,
        minHeight: 'auto',
        '&:hover': {
          backgroundColor: colors.graytone50,
        },
        '&:active': {
          backgroundColor: colors.graytone100,
        },
      },
    },
  },
  MuiRadio: {
    styleOverrides: {
      root: {
        '&:hover': {
          backgroundColor: colors.graytone50,
        },
        '&.Mui-checked:hover': {
          backgroundColor: colors.orange50,
        },
      },
    },
  },
  MuiAutocomplete: {
    styleOverrides: {
      root: {
        padding: 0,
        '& .MuiFilledInput-root': {
          paddingTop: 16,
          borderRadius: 0,
        },
        '& .MuiOutlinedInput-root': {
          borderRadius: 0,
        },
        '& .MuiAutocomplete-tag': {
          backgroundColor: colors.graytone100,
          color: colors.graytone600,
          marginInline: 2,
          paddingBlock: 1,
          paddingInline: '8px 2px',
          borderRadius: '12px',
          '& .MuiChip-label': {
            paddingInline: 1,
          },
          '& .MuiChip-deleteIcon': {
            fontSize: 16,
            marginLeft: 4,
            backgroundColor: 'transparent',
            color: colors.graytone600,
            '&:hover': {
              color: colors.graytone700,
            },
          },
        },
      },
      paper: {
        boxShadow: 'rgba(149, 157, 165, 0.2) 0px 8px 24px;',
      },
    },
  },
  MuiSelect: {
    styleOverrides: {
      root: {
        padding: '12px 0',
        color: colors.graytone400,
        borderRadius: 0,
        '& .MuiOutlinedInput-notchedOutline': {
          border: 'none',
          borderBottom: `1px solid ${colors.graytone600}`,
          borderRadius: 0,
        },
        '&:hover': {
          backgroundColor: colors.graytone900,
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none',
            borderBottom: `1px solid ${colors.graytone400}`,
          },
        },
        '&.Mui-focused': {
          backgroundColor: colors.graytone950,
          color: colors.white,
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none',
            borderBottom: `1px solid ${colors.white}`,
          },
        },
        '&.Mui-error': {
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none',
            borderBottom: `1px solid ${colors.orange500}`,
          },
          backgroundColor: colors.orange950,
          color: colors.orange500,
        },
        '&.has-value': {
          color: colors.white,
        },
        '&.has-label': {
          padding: '25px 0 8px',
        },
      },
      select: {
        padding: 0,
        display: 'flex',
        alignItems: 'center',
      },
    },
  },
  MuiAccordion: {
    styleOverrides: {
      root: {
        padding: 0,
        margin: '16px 0px',
        borderRadius: 24,
        '&:before': {
          display: 'none',
        },
        '&.Mui-expanded': {
          borderRadius: 24,
        },
        '&:first-of-type': {
          borderRadius: 24,
          marginTop: 0,
        },
        '&:last-of-type': {
          borderRadius: 24,
          marginBottom: 0,
        },
      },
    },
  },
  MuiAccordionSummary: {
    styleOverrides: {
      root: {
        padding: 8,
        minHeight: 0,
        backgroundColor: colors.graytone950,
        borderRadius: 24,
        color: colors.graytone600,
        '&.Mui-expanded': {
          minHeight: 'auto',
        },
      },
      content: {
        margin: 0,
        '&.Mui-expanded': {
          margin: 0,
        },
      },
    },
  },
  MuiAccordionDetails: {
    styleOverrides: {
      root: {
        marginTop: 8,
      },
    },
  },
  MuiStepConnector: {
    styleOverrides: {
      root: {
        '&.Mui-active': {
          '& .MuiStepConnector-line': {
            borderColor: colors.green500,
          },
        },
        '&.Mui-completed': {
          '& .MuiStepConnector-line': {
            borderColor: colors.green500,
          },
        },
      },
      line: {
        borderColor: colors.graytone100,
        borderTopWidth: 3,
      },
    },
  },
  MuiPopover: {
    styleOverrides: {
      paper: {
        boxShadow: `0px 4px 15px 0px rgba(${hexToRgb(colors.black)}, 0.05)`,
      },
    },
  },
  MuiDivider: {
    styleOverrides: {
      root: {
        '&.MuiDivider-root': {
          margin: 4,
          backgroundColor: colors.graytone800,
        },
      },
    },
  },
  MuiSwitch: {
    styleOverrides: {
      root: {
        padding: 9,
      },
      thumb: {
        boxShadow: 'none',
        width: 14,
        height: 14,
        margin: 3,
        backgroundColor: colors.white,
      },
      track: {
        borderRadius: 11,
        backgroundColor: colors.graytone600,
        opacity: 1,
      },
      switchBase: {
        '&.Mui-checked + .MuiSwitch-track': {
          backgroundColor: colors.blue500,
          opacity: 1,
        },
        '&.Mui-checked.Mui-disabled + .MuiSwitch-track': {
          opacity: 0.5,
        },
      },
    },
  },
};

export default overrides;
